import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import { PlayArrow, PictureAsPdfOutlined, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Header from '../components/common/Header';
import { fetchActiveProducts } from '../store/productsSlice';
import { addToCart } from '../store/cartSlice';
import { formatPriceWithCurrency } from '../utils/formatPrice';
import ValEdIcon from '../components/common/ValEdIcon';

const ArticleList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items: products, loading } = useSelector((state) => state.products);
  const { items: cartItems } = useSelector((state) => state.cart);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, physical, virtual
  const [filterPrice, setFilterPrice] = useState('all'); // all, free, paid

  useEffect(() => {
    if (!products || products.length === 0) {
      dispatch(fetchActiveProducts());
    }
  }, [dispatch, products]);

  // Filtrer les produits
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || product.product_type === filterType;
    
    const matchesPrice = filterPrice === 'all' ||
      (filterPrice === 'free' && product.is_free) ||
      (filterPrice === 'paid' && !product.is_free);

    return matchesSearch && matchesType && matchesPrice;
  });

  const handleProductClick = (productId) => {
    navigate(`/article/${productId}`);
  };

  const handleAddToValEd = (e, product) => {
    e.stopPropagation(); // Empêcher la navigation vers les détails
    
    // Vérifier si c'est un produit physique
    if (product.product_type !== 'physical') {
      toast.info('Seuls les produits physiques peuvent être ajoutés à la  ValEd');
      return;
    }
    
    // Vérifier le stock
    if (product.stock === 0) {
      toast.error('Produit en rupture de stock');
      return;
    }
    
    dispatch(addToCart({ product, quantity: 1 }));
    toast.success(`${product.title} ajouté au ValEd !`);
  };

  if (loading) {
    return (
      <Box  className="mobile-app-container has-fixed-header" sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
        <Header />
        <Container sx={{ py: 4, textAlign: 'center' }}>
          <Typography>Chargement des produits...</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="mobile-app-container has-fixed-header"   sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
      <Header />
      
      <Container sx={{ py: { xs: 2, md: 4 } }}>
        {/* En-tête */}
        <Box sx={{ mb: 4 }}>
          <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            fontFamily: 'Achiko',
            color: '#784e3c',
            mb: 3,
            textAlign: 'center',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
          }}
        >
          Boutique baoubab
        </Typography>
          <Typography variant="body1" color="text.secondary">
            Découvrez tous nos produits éducatifs pour vos enfants
          </Typography>
        </Box>

        {/* Barre de recherche */}
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un produit (nom ou description)"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#784e3c' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: '999px',
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#784e3c'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#784e3c'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#20BA5A',
                  borderWidth: 2
                }
              }
            }}
            inputProps={{
              style: {
                fontFamily: 'Achiko',
                color: '#333'
              }
            }}
          />
        </Box>

        {/* Filtres */}
        <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', justifyContent: 'center' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="filter-type-label">Type de produit</InputLabel>
            <Select
              labelId="filter-type-label"
              id="filter-type"
              value={filterType}
              label="Type de produit"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">Tous les produits</MenuItem>
              <MenuItem value="physical">Produits physiques</MenuItem>
              <MenuItem value="virtual">Produits virtuels</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="filter-price-label">Prix</InputLabel>
            <Select
              labelId="filter-price-label"
              id="filter-price"
              value={filterPrice}
              label="Prix"
              onChange={(e) => setFilterPrice(e.target.value)}
            >
              <MenuItem value="all">Tous les prix</MenuItem>
              <MenuItem value="free">Gratuit</MenuItem>
              <MenuItem value="paid">Payant</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Grille de produits */}
        {filteredProducts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Aucun produit trouvé
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 2, md: 3 },
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
            }}
          >
            {filteredProducts.map((product) => (
              <Box key={product.id} sx={{ display: 'flex' }}>
                <Card
                  elevation={3}
                  sx={{
                    width: '100%',
                    minHeight: 420,
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    flex: 1,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
                    }
                  }}
                  onClick={() => handleProductClick(product.id)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={product.image_url }
                      alt={product.title}
                      sx={{ objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = product.image_url;
                      }}
                    />
                    
                    {/* Badges en haut à droite */}
                    <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                      {product.is_free && (
                        <Chip
                          label="GRATUIT"
                          size="small"
                          sx={{
                            bgcolor: '#4CAF50',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                      {!product.is_free && product.product_type === 'virtual' && (
                        <Chip
                          label="PREMIUM"
                          size="small"
                          sx={{
                            bgcolor: '#FF9800',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>

                    {/* Badges en haut à gauche */}
                    <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={product.product_type === 'physical' ? ' Physique' : ' Virtuel'}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.9)',
                          fontSize: '0.7rem'
                        }}
                      />
                      {/* Badge type de contenu virtuel */}
                      {product.product_type === 'virtual' && product.virtual_type && (
                        <Chip
                          icon={product.virtual_type === 'video' ? <PlayArrow sx={{ fontSize: '0.8rem' }} /> : <PictureAsPdfOutlined sx={{ fontSize: '0.8rem' }} />}
                          label={product.virtual_type === 'video' ? 'VIDÉO' : 'PDF'}
                          size="small"
                          sx={{
                            bgcolor: product.virtual_type === 'video' ? '#FF5722' : '#2196F3',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            px: { xs: 2, sm: 3 },
                            py: { xs: 0.5, sm: 1 },
                            '&:hover': {
                              bgcolor: product.virtual_type === 'video' ? '#FF5722' : '#2196F3'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="div"
                      sx={{
                        fontWeight: 'bold',
                        fontFamily: 'Achiko',
                        fontSize: '1.1rem',
                        minHeight: '3em',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {product.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {product.description || 'Aucune description disponible'}
                    </Typography>

                    <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #eee', minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {product.product_type === 'physical' ? (
                        (() => {
                          const isInValEd = cartItems?.some((item) => item.product.id === product.id);
                          const isOutOfStock = product.stock === 0;
                          const baseBg = isOutOfStock ? '#f5f5f5' : isInValEd ? '#20BA5A' : '#784e3c';
                          const hoverBg = isOutOfStock ? '#f5f5f5' : isInValEd ? '#1a9b4d' : '#3a9bc7';

                          return (
                            <>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: product.is_free ? '#4CAF50' : '#784e3c',
                                  fontWeight: 'bold',
                                  fontFamily: 'Achiko'
                                }}
                              >
                                {product.is_free ? 'Gratuit' : formatPriceWithCurrency(product.price)}
                              </Typography>
                              
                              <Tooltip title={isInValEd ? 'Déjà dans la ValEd' : 'Ajouter à la ValEd'}>
                                <span>
                                  <IconButton
                                    onClick={(e) => handleAddToValEd(e, product)}
                                    disabled={isOutOfStock}
                                    sx={{
                                      bgcolor: baseBg,
                                      color: isOutOfStock ? '#999' : '#fff',
                                      transition: 'background-color 0.2s ease',
                                      '&:hover': {
                                        bgcolor: hoverBg
                                      },
                                      '&.Mui-disabled': {
                                        bgcolor: '#f5f5f5',
                                        color: '#999'
                                      }
                                    }}
                                  >
                                    <ValEdIcon
                                      size={20}
                                      color={isOutOfStock ? '#999' : '#fff'}
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          );
                        })()
                      ) : (
                        <Chip
                          label={product.is_free ? 'Accès gratuit' : 'Premium'}
                          size="small"
                          sx={{
                            bgcolor: product.is_free ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)',
                            color: product.is_free ? '#2E7D32' : '#E65100',
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ArticleList;
